<script setup>
/* global $ */
import { computed, ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import useUser from '../../../../../Composables/User/useUser.ts'
import { useI18n } from 'vue-i18n'
import FieldLabel from './FieldLabel.vue'
import SelectOption from './SelectOption.vue'
import { useFormField } from '../../../../../Composables/useFormField.ts'
import { buildFieldKey } from '../../../Utils/FieldUtils.ts'
import { useFormFieldDef } from '../../../../../Composables/useFormFieldDef.js'
import { getOptionValue } from '../../../../../Utils/selectOptionUtils'
import { normalizeInitialValue } from '../../../../../Utils/selectUtils'

const props = defineProps({
  field: { type: Object, required: false, default: null },
  inputId: { type: String, required: false, default: null },
  formData: { type: Object, required: false, default: null },
  formRef: { type: String, required: false, default: '' },
  name: { type: String, required: true },
  valueProperty: { type: String, required: false, default: 'id' },
  labelProperty: { type: String, required: false, default: 'name' },
  templateSelectionRender: { type: Function, required: false, default: null },
  labelTop: { type: Boolean, default: false },
  domain: { type: String, default: null },
  containerClass: { type: String, required: false, default: '' },
  typeValue: { type: String, required: false, default: null },
  choices: { type: Array, required: false, default: null },
  specificLabel: { type: String, default: null },
  multiple: { type: Boolean, default: false },
  labelWrapperClasses: { type: String, default: null },
  inputWrapperClasses: { type: String, default: null },
})

const selectElement = ref(null)
const { isReadOnly } = useUser()
const emit = defineEmits(['blur'])

const { locale, t, te } = useI18n()
const { value: fieldValue, hasValue, parseValue } = useFormField(props.formData, props.name, props.typeValue)

function buildSelect2Options(fieldDef, templateSelectionRender, hasValue, locale, selectElement, placeholder) {
  const field = fieldDef || {}
  const isRequired = field.options?.required === true
  const isDisabled = field.options?.disabled === true || isReadOnly.value
  const canClear = !isDisabled && (!(isRequired && hasValue) ? true : !isRequired || !hasValue)

  const optionComplement = {
    allowClear: canClear,
    templateSelection: templateSelectionRender,
    allowHtml: true,
    language: locale,
    closeOnSelect: false,
    disabled: isDisabled,
    placeholder: {
      id: '',
      text: placeholder,
    },
  }

  if (field.options && field.options.width === undefined) {
    optionComplement.width = '100%'
  }

  try {
    const el = selectElement
    const parentModal = el?.closest('.modal')?.id
    if (parentModal !== undefined && parentModal) {
      const escapedModalId = parentModal.replace(/\./g, '\\.')
      optionComplement.dropdownParent = $(`#${escapedModalId}`)
    }
  } catch {}

  if (field.options?.ajax) {
    optionComplement.ajax = field.options.ajax
  }

  if (field.options?.['data-tags']) {
    optionComplement.tags = true
    optionComplement.createTag = (params) => {
      const term = params.term
      if (!term) {
        return null
      }
      return { id: term, text: term, newTag: true }
    }
  }

  if (field.options?.multiple) {
    optionComplement.theme = 'default'
  }

  if (!field.options?.multiple) {
    optionComplement.closeOnSelect = true
  }

  return { ...(field.options || {}), ...optionComplement }
}

async function initSelect2(element, options, initialValue, onChange) {
  if (!element) {
    return
  }

  await nextTick()

  $(element).select2(options)

  if (initialValue !== null && initialValue !== undefined) {
    setSelect2Value(element, initialValue)
  }

  const getCurrentValue = () => {
    const multiple = element.multiple
    if (multiple) {
      return Array.from(element.selectedOptions)
        .map((opt) => opt.value)
        .filter((v) => v !== '')
    }
    return element.value === '' ? null : element.value
  }

  $(element).on('select2:select select2:unselect', () => {
    if (onChange) {
      onChange(getCurrentValue())
    }
  })
}

function setSelect2Value(element, value) {
  if (!element) {
    return
  }

  if (Array.isArray(value)) {
    Array.from(element.options).forEach((opt) => {
      opt.selected = value.includes(opt.value)
    })
  } else {
    element.value = value === null ? '' : String(value)
  }

  $(element).trigger('change.select2')
}

function destroySelect2(element) {
  if (!element) {
    return
  }

  try {
    $(element).select2('destroy')
  } catch {}
}

const fieldComputed = computed(() => {
  if (props.field) return props.field
  if (props.formData && props.formData.def && props.name) return props.formData.def[props.name]
  return null
})

const { inputId } = useFormFieldDef(props.formData, props.name, {
  inputId: props.inputId,
  formRef: props.formRef,
})

const choicesComputed = computed(() => {
  if (props.choices !== null) {
    return props.choices
  }
  if (!fieldComputed.value) {
    return []
  }
  if (fieldComputed.value.options && fieldComputed.value.options.choices) {
    return fieldComputed.value.options.choices
  }
  return []
})

const valuePropertyComputed = computed(() => {
  return props.valueProperty || 'id'
})

const labelPropertyComputed = computed(() => {
  return props.labelProperty || 'name'
})

const normalizedValue = computed(() => {
  const val = fieldValue.value
  if (props.multiple) {
    if (Array.isArray(val)) {
      return val
    }
    return val ? [val] : []
  }
  return val
})

const normalizedSingleValue = computed(() => {
  const val = fieldValue.value
  if (!val) {
    return null
  }

  return typeof val === 'object' ? getOptionValue(val) : val
})

const placeholderText = computed(() => {
  const placeholderKey = buildFieldKey(props.name, props.domain, { suffix: 'placeholder' })
  return te(placeholderKey) ? t(placeholderKey) : t('main.selectPlaceholder')
})

function refreshDisplay() {
  destroySelect2(selectElement.value)
  setupSelect2()
}

function setupSelect2() {
  const el = selectElement.value
  if (!el) {
    return
  }

  const options = buildSelect2Options(
    fieldComputed.value,
    props.templateSelectionRender,
    hasValue.value,
    locale.value,
    el,
    placeholderText.value,
  )
  const initialValue = fieldValue.value
  const normalized = normalizeInitialValue(initialValue)

  initSelect2(el, options, normalized, (value) => {
    const parsed = parseValue(value)
    fieldValue.value = parsed
  })
}

function isSelected(option) {
  if (props.multiple) {
    return (normalizedValue.value || []).includes(String(option.id || option.value || ''))
  }

  return String(normalizedSingleValue.value) === String(option.id || option.value || '')
}

onMounted(() => {
  if (!props.formData) {
    return
  }

  props.formData.datas[props.name] = props.multiple ? [] : null

  if (props.formData.object && props.name in props.formData.object) {
    const initialValue = props.formData.object[props.name]
    if (Array.isArray(initialValue)) {
      props.formData.datas[props.name] = initialValue.map((item) => getOptionValue(item))
    } else if (initialValue) {
      props.formData.datas[props.name] = getOptionValue(initialValue)
    }
  }

  setupSelect2()
})

onBeforeUnmount(() => {
  destroySelect2(selectElement.value)
})

watch(
  () => fieldValue.value,
  (newVal) => {
    const normalized = normalizeInitialValue(newVal)
    if (selectElement.value && normalized) {
      setSelect2Value(selectElement.value, normalized)
    }
  },
)

defineExpose({
  refreshDisplay,
  selectElement,
})
</script>

<template>
  <div v-if="fieldComputed" :class="containerClass">
    <div class="row align-items-center">
      <FieldLabel
        :domain="domain"
        :name="name"
        :wrapper-classes="labelWrapperClasses"
        :field="fieldComputed"
        :specific-label="specificLabel"
      />
      <div :class="inputWrapperClasses">
        <div class="d-flex align-items-start">
          <select
            :id="inputId"
            ref="selectElement"
            v-model="fieldValue"
            style="flex: 1; min-width: 0; max-width: 100%"
            :disabled="fieldComputed?.options?.disabled"
            :multiple="props.multiple"
            :e2e-select="name"
            @blur="emit('blur')"
          >
            <SelectOption
              v-for="opt in choicesComputed"
              :key="`${opt.id || opt.value}`"
              :data="opt"
              :value-property="valuePropertyComputed"
              :label-property="labelPropertyComputed"
              :selected="isSelected(opt)"
            />
          </select>
          <slot name="end"></slot>
        </div>
      </div>
    </div>
  </div>
</template>
